import type React from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { createContext, useContext, useId } from "react";
import { openHelp } from "../lib/open-help.ts";
import { Eyebrow } from "./brand.tsx";

/** True inside the staff shell, where the sidebar sits on the left on laptops. */
export const InShell = createContext(false);

/**
 * One screen, laid out like the brand guide's pages: an uppercase section
 * label, a bold Poppins title, a short intro, then white rounded cards.
 */
export function Page({
	section,
	title,
	intro,
	wide = false,
	bare = false,
	action,
	children,
}: {
	section?: string;
	title: string;
	intro?: ReactNode;
	/** The page's main button (Add a ___): top right on laptops, under the intro on phones. One place on every page. */
	action?: ReactNode;
	wide?: boolean;
	/** Children render without the white card (for pages that lay out their own cards). */
	bare?: boolean;
	children: ReactNode;
}) {
	// Beside the sidebar, every page sits in the same centred column as the "New here?"
	// banner, so titles never jump about between pages. Narrow pages (forms, settings)
	// keep their content to a readable width from that same left edge.
	const inShell = useContext(InShell);
	const width = wide || inShell ? "max-w-[64rem] lg:max-w-[96rem]" : "max-w-[34rem]";
	const body = !wide && inShell ? "max-w-[42rem]" : "";
	return (
		<main className={`mx-auto w-full px-4 pt-6 pb-16 lg:px-8 lg:pt-8 ${width}`}>
			<div className={`flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between ${body}`}>
				<div className="min-w-0">
					{section ? <Eyebrow>{section}</Eyebrow> : null}
					<h1 className="mt-1 font-display text-[1.875rem] leading-[1.15] font-bold">{title}</h1>
					{intro ? <p className="mt-2 max-w-[70ch] text-ink-soft leading-relaxed">{intro}</p> : null}
				</div>
				{action ? <div className="flex shrink-0 flex-wrap gap-3">{action}</div> : null}
			</div>
			<div className={`mt-6 ${body}`}>{bare ? children : <Card>{children}</Card>}</div>
		</main>
	);
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
	return <div className={`rounded-[var(--radius-card)] bg-card p-5 sm:p-6 ${className}`}>{children}</div>;
}

/*
 * The kit's rules (docs/DESIGN.md). The signature comes from the SSG agency pass:
 * everything you can press has a coloured ledge along its bottom edge, which sinks
 * when pressed. Things you can't press (status pills, cards) never have one, so the
 * ledge itself tells people what is tappable. Everything you tap or type into is
 * 48px tall; fields have rounded corners; buttons and chips are pills.
 */
const press = "transition-[translate,box-shadow] duration-75 active:translate-y-0.5 motion-reduce:transition-none";
export const fieldClass =
	"block min-h-12 w-full min-w-0 rounded-xl border-2 border-b-4 border-field bg-card px-3 text-base text-ink focus:border-purple aria-[invalid]:border-stop disabled:opacity-60 dark:focus:border-mint";
export const primaryClass = `inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-action px-6 font-display text-base font-semibold text-action-ink shadow-[inset_0_-4px_0_var(--color-ledge)] hover:bg-action-hover active:shadow-[inset_0_-2px_0_var(--color-ledge)] disabled:opacity-60 ${press}`;
export const secondaryClass = `inline-flex min-h-12 items-center justify-center gap-2 rounded-full border-2 border-b-4 border-navy bg-card px-5 font-semibold text-ink hover:bg-sheet active:border-b-2 disabled:opacity-50 dark:border-mint ${press}`;
export const quietClass =
	"inline-flex min-h-12 items-center justify-center gap-2 px-2 font-semibold text-link underline underline-offset-4 hover:no-underline disabled:cursor-default disabled:text-ink-soft disabled:no-underline disabled:opacity-60";
/** Quiet, but it takes something away (Reject, Remove, Take away access): red, so it never looks like any other link. */
export const dangerClass = quietClass.replace("text-link", "text-stop");
export const textLinkClass = "font-semibold text-link underline underline-offset-4 hover:no-underline";
/** A pill you can switch on (filters, saved views, weeks of a cycle). On: mint with a purple ledge, like the pass. */
export const chipClass = `inline-flex min-h-12 shrink-0 cursor-pointer items-center gap-2 rounded-full border-2 border-b-4 border-field bg-card px-4 font-semibold text-ink active:border-b-2 aria-pressed:border-purple aria-pressed:bg-mint aria-pressed:text-navy has-[:checked]:border-purple has-[:checked]:bg-mint has-[:checked]:text-navy has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-[var(--color-focus)] ${press}`;

/** The main action on a navy panel (bulk bar, sticky bar): mint with a purple ledge. */
export const onNavyClass = `inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-mint px-5 font-semibold text-navy shadow-[inset_0_-4px_0_var(--color-purple)] active:shadow-[inset_0_-2px_0_var(--color-purple)] disabled:opacity-60 ${press}`;

/** A big card you press to go somewhere (Home tiles, agencies): it has the ledge too. */
export const tileClass = `flex items-center gap-4 rounded-[var(--radius-card)] border-2 border-b-4 border-rule bg-card p-4 hover:border-purple active:border-b-2 sm:p-5 ${press}`;

export type ButtonVariant = "primary" | "secondary" | "quiet" | "danger";
const BUTTON_LOOK: Record<ButtonVariant, string> = {
	primary: primaryClass,
	secondary: secondaryClass,
	quiet: quietClass,
	danger: dangerClass,
};
export const buttonClass = (variant: ButtonVariant = "primary") => BUTTON_LOOK[variant];

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant };

/** Primary: the one main action on a screen. Secondary: other actions. Quiet: small, link-like actions. Danger: a quiet one that takes something away, and asks first or can be undone. */
export function Button({ variant = "primary", className = "", ...props }: ButtonProps) {
	return <button type="button" className={`${BUTTON_LOOK[variant]} ${className}`} {...props} />;
}

/** A button that only switches something on or off, shown as a pill (aria-pressed). */
export function ToggleChip({
	pressed,
	className = "",
	...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { pressed: boolean }) {
	return <button type="button" aria-pressed={pressed} className={`${chipClass} ${className}`} {...props} />;
}

interface FieldExtras {
	label: string;
	hint?: string;
	error?: string | null;
	/** Dense rows (a week of shifts): the label is read out but not shown. Use sparingly. */
	labelHidden?: boolean;
	/** No space below, for fields laid out in a row. */
	tight?: boolean;
	/** Says "(optional)" after the label, so nobody wonders whether they must fill it in. */
	optional?: boolean;
}
type FieldProps = InputHTMLAttributes<HTMLInputElement> & FieldExtras;

function FieldLabel({ id, label, hidden }: { id: string; label: string; hidden?: boolean }) {
	return (
		<label htmlFor={id} className={hidden ? "sr-only" : "block font-bold"}>
			{label}
		</label>
	);
}

/** A labelled text box. Label above, hint under the label, error under the box. */
export function Field({ label, hint, error, labelHidden, tight, optional, className = "", ...input }: FieldProps) {
	const id = useId();
	const describedBy = [hint ? `${id}-hint` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ") || undefined;
	return (
		<div className={tight ? "" : "mb-5"}>
			<FieldLabel id={id} label={optional ? `${label} (optional)` : label} hidden={labelHidden} />
			{hint ? (
				<p id={`${id}-hint`} className="mt-1 text-sm text-ink-soft">
					{hint}
				</p>
			) : null}
			<input
				id={id}
				aria-invalid={error ? true : undefined}
				aria-describedby={describedBy}
				className={`${labelHidden ? "" : "mt-2"} ${fieldClass} ${className}`}
				{...input}
			/>
			{error ? (
				<p id={`${id}-error`} className="mt-2 text-sm font-semibold text-stop">
					{error}
				</p>
			) : null}
		</div>
	);
}

/**
 * A message about what just happened. Give a problem a `helpSlug` and it gets a
 * "Help with this" link that opens that help article in the Help drawer.
 */
export function Alert({
	tone = "stop",
	helpSlug,
	children,
}: {
	/** stop: a problem. clear: it worked. note: worth knowing before you start, like who can change this. */
	tone?: "stop" | "clear" | "note";
	helpSlug?: string;
	children: ReactNode;
}) {
	const look = { stop: "border-stop text-stop", clear: "border-clear text-clear", note: "border-purple text-ink" }[
		tone
	];
	return (
		<div
			role={tone === "stop" ? "alert" : "status"}
			className={`mb-5 rounded-xl border-l-4 bg-sheet px-3 py-2 font-semibold ${look}`}
		>
			<p>{children}</p>
			{helpSlug ? (
				<button type="button" onClick={() => openHelp({ slug: helpSlug })} className={`mt-1 min-h-11 ${textLinkClass}`}>
					Help with this
				</button>
			) : null}
		</div>
	);
}

type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> &
	FieldExtras & {
		options: readonly { value: string | number; label: string }[];
		placeholder?: string;
	};

/** A labelled dropdown: for choosing one of many (more than five) options. */
export function Select({
	label,
	hint,
	options,
	error,
	placeholder,
	labelHidden,
	tight,
	className = "",
	...select
}: SelectProps) {
	const id = useId();
	return (
		<div className={tight ? "" : "mb-5"}>
			<FieldLabel id={id} label={label} hidden={labelHidden} />
			{hint ? (
				<p id={`${id}-hint`} className="mt-1 text-sm text-ink-soft">
					{hint}
				</p>
			) : null}
			<select
				id={id}
				aria-invalid={error ? true : undefined}
				aria-describedby={[hint ? `${id}-hint` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ") || undefined}
				className={`${labelHidden ? "" : "mt-2"} ${fieldClass} ${className}`}
				{...select}
			>
				{placeholder !== undefined ? <option value="">{placeholder}</option> : null}
				{options.map((o) => (
					<option key={o.value} value={o.value}>
						{o.label}
					</option>
				))}
			</select>
			{error ? (
				<p id={`${id}-error`} className="mt-2 text-sm font-semibold text-stop">
					{error}
				</p>
			) : null}
		</div>
	);
}

/** Status shown as text in a pill; colour is never the only signal (WCAG 1.4.1). */
export function Pill({ tone = "plain", children }: { tone?: "good" | "plain" | "warn"; children: ReactNode }) {
	const look =
		tone === "good" ? "bg-mint text-navy" : tone === "warn" ? "border-2 border-stop text-stop" : "bg-sheet text-ink";
	return (
		<span
			className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold whitespace-nowrap ${look}`}
		>
			{children}
		</span>
	);
}

/** Nothing here yet: says so, and what to do next. The one empty look on every page. */
export function Empty({
	title,
	children,
	action,
	className = "",
}: {
	title: string;
	children?: ReactNode;
	action?: ReactNode;
	className?: string;
}) {
	return (
		<div className={`rounded-[var(--radius-card)] border-2 border-dashed border-rule p-6 text-center ${className}`}>
			<p className="font-display text-lg font-bold">{title}</p>
			{children ? <div className="mt-2 text-ink-soft">{children}</div> : null}
			{action ? <div className="mt-4 flex flex-wrap justify-center gap-3">{action}</div> : null}
		</div>
	);
}

/** A labelled multi-line box, for notes. */
export function TextArea({
	label,
	hint,
	error,
	labelHidden,
	tight,
	className = "",
	...area
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & FieldExtras) {
	const id = useId();
	const describedBy = [hint ? `${id}-hint` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ") || undefined;
	return (
		<div className={tight ? "" : "mb-5"}>
			<FieldLabel id={id} label={label} hidden={labelHidden} />
			{hint ? (
				<p id={`${id}-hint`} className="mt-1 text-sm text-ink-soft">
					{hint}
				</p>
			) : null}
			<textarea
				id={id}
				rows={4}
				aria-invalid={error ? true : undefined}
				aria-describedby={describedBy}
				className={`${labelHidden ? "" : "mt-2"} ${fieldClass} py-2 ${className}`}
				{...area}
			/>
			{error ? (
				<p id={`${id}-error`} className="mt-2 text-sm font-semibold text-stop">
					{error}
				</p>
			) : null}
		</div>
	);
}

/** On or off. The whole row is the target, the box is 24px, and the words say what ticking means. */
export function Checkbox({
	label,
	hint,
	className = "",
	...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { label: ReactNode; hint?: ReactNode }) {
	return (
		<label className={`flex min-h-12 cursor-pointer items-start gap-3 py-2.5 ${className}`}>
			<input type="checkbox" className="kit-check mt-0.5 shrink-0" {...input} />
			<span className="min-w-0">
				<span className="font-semibold">{label}</span>
				{hint ? <span className="block text-sm text-ink-soft">{hint}</span> : null}
			</span>
		</label>
	);
}

/** Pick one of a few (two to five) options, each with room for a short explanation. */
export function RadioList<V extends string>({
	legend,
	name,
	options,
	value,
	onChange,
	legendHidden = false,
	cards = false,
}: {
	legend: string;
	name: string;
	options: readonly { value: V; label: ReactNode; hint?: ReactNode }[];
	value: V;
	onChange: (v: V) => void;
	legendHidden?: boolean;
	/** Each option in its own card: for a first, important choice ("What's in the file?"). */
	cards?: boolean;
}) {
	const row = cards
		? "mb-2 flex min-h-14 cursor-pointer items-start gap-3 rounded-xl border-2 border-b-4 border-field bg-card p-3 has-[:checked]:border-purple has-[:checked]:bg-mint/30"
		: "flex min-h-12 cursor-pointer items-start gap-3 py-2.5";
	return (
		<fieldset className="mb-5 min-w-0">
			<legend className={legendHidden ? "sr-only" : "mb-2 font-bold"}>{legend}</legend>
			{options.map((o) => (
				<label key={o.value} className={row}>
					<input
						type="radio"
						name={name}
						value={o.value}
						checked={value === o.value}
						onChange={() => onChange(o.value)}
						className="kit-radio mt-0.5 shrink-0"
					/>
					<span className="min-w-0">
						<span className="font-semibold">{o.label}</span>
						{o.hint ? <span className="block text-sm text-ink-soft">{o.hint}</span> : null}
					</span>
				</label>
			))}
		</fieldset>
	);
}

/** Pick one, shown as pills that wrap (never scroll sideways). For filters and short choices. */
export function ChoiceChips({
	name,
	options,
	value,
	onChange,
	legend,
	legendHidden = false,
}: {
	name: string;
	options: readonly { value: string; label: string }[];
	value: string;
	onChange: (v: string) => void;
	legend: string;
	legendHidden?: boolean;
}) {
	return (
		<fieldset className="min-w-0">
			<legend className={legendHidden ? "sr-only" : "mb-2 font-bold"}>{legend}</legend>
			<div className="flex flex-wrap gap-2">
				{options.map((o) => (
					<label key={o.value} className={chipClass}>
						<input
							type="radio"
							name={name}
							value={o.value}
							checked={value === o.value}
							onChange={() => onChange(o.value)}
							className="sr-only"
						/>
						{o.label}
					</label>
				))}
			</div>
		</fieldset>
	);
}

/** A card with a heading and, optionally, one action on the right ("Add a task"). */
export function Section({
	title,
	action,
	children,
	className = "",
	id,
}: {
	title: string;
	action?: ReactNode;
	children: ReactNode;
	className?: string;
	id?: string;
}) {
	const autoId = useId();
	const headingId = id ?? autoId;
	return (
		<section aria-labelledby={headingId} className={`rounded-[var(--radius-card)] bg-card p-5 sm:p-6 ${className}`}>
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h2 id={headingId} className="font-display text-xl font-bold">
					{title}
				</h2>
				{action ? <div className="-mr-2">{action}</div> : null}
			</div>
			<div className="mt-2">{children}</div>
		</section>
	);
}

/** How far along something is, as a bar and in words (colour is never the only signal). */
export function ProgressBar({
	value,
	max,
	label,
	caption,
}: {
	value: number;
	max: number;
	label: string;
	caption?: ReactNode;
}) {
	const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
	return (
		<div className="min-w-32">
			<div
				role="progressbar"
				aria-valuemin={0}
				aria-valuemax={max}
				aria-valuenow={value}
				aria-label={label}
				className="h-2.5 overflow-hidden rounded-full bg-sheet"
			>
				<div className={`h-full rounded-full ${pct === 100 ? "bg-clear" : "bg-purple"}`} style={{ width: `${pct}%` }} />
			</div>
			{caption ? <span className="mt-1 block text-sm text-ink-soft tabular-nums">{caption}</span> : null}
		</div>
	);
}

/**
 * A panel that slides over the page from the right (a full screen on phones):
 * keyboard focus stays inside, Esc closes it, and the page behind is dimmed.
 */
export function Drawer({
	title,
	onClose,
	children,
	intro,
	wide = false,
}: {
	title: string;
	onClose: () => void;
	children: ReactNode;
	intro?: ReactNode;
	/** Room for a week of times. */
	wide?: boolean;
}) {
	const id = useId();
	return (
		<dialog
			ref={(el) => {
				if (el && !el.open) el.showModal();
			}}
			onClose={onClose}
			aria-labelledby={id}
			className={`m-0 ml-auto h-dvh max-h-dvh w-full ${wide ? "max-w-[44rem]" : "max-w-[28rem]"} overflow-y-auto bg-card p-5 text-ink shadow-[0_0_32px_rgba(35,35,95,0.3)] backdrop:bg-navy/40`}
		>
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h2 id={id} className="font-display text-xl font-bold">
					{title}
				</h2>
				<Button variant="quiet" onClick={onClose}>
					Close
				</Button>
			</div>
			{intro ? <p className="text-sm text-ink-soft">{intro}</p> : null}
			<div className="mt-3">{children}</div>
		</dialog>
	);
}

/** Table cells for lists on laptops: the same padding, header style and row hover everywhere. */
export const table = {
	wrap: "overflow-x-auto rounded-[var(--radius-card)] bg-card",
	table: "w-full text-left",
	head: "border-b border-rule text-sm text-ink-soft",
	// Phones keep the outer edges but close the gaps between columns, so more fits.
	th: "px-2 py-3 font-semibold whitespace-nowrap first:pl-4 last:pr-4 sm:px-4",
	row: "border-b border-rule last:border-0 hover:bg-mint/15",
	td: "px-2 py-3 first:pl-4 last:pr-4 sm:px-4",
	/** A column phones leave out, so the table fits without scrolling sideways. Keep the name and the key number. */
	wideOnly: "max-sm:hidden",
} as const;

/**
 * Choose a file or take a photo: a big dashed target with the chosen file's name
 * in it, so it is obvious something was picked.
 */
export function FilePicker({
	label,
	hint,
	prompt = "Choose a file",
	chosen,
	icon,
	className = "",
	...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
	label: string;
	hint?: string;
	prompt?: string;
	chosen?: string | null;
	icon: ReactNode;
}) {
	const id = useId();
	return (
		<div className={`mb-5 ${className}`}>
			<label htmlFor={id} className="block font-bold">
				{label}
			</label>
			{hint ? <p className="mt-1 text-sm text-ink-soft">{hint}</p> : null}
			<label
				htmlFor={id}
				className="mt-2 flex min-h-16 cursor-pointer items-center gap-3 rounded-xl border-2 border-b-4 border-dashed border-field bg-card px-4 hover:border-purple focus-within:outline-3 focus-within:outline-offset-2 focus-within:outline-[var(--color-focus)]"
			>
				<span aria-hidden="true" className="shrink-0 text-purple">
					{icon}
				</span>
				<span className={`min-w-0 break-words ${chosen ? "font-semibold" : "text-ink-soft"}`}>{chosen ?? prompt}</span>
				<input id={id} type="file" className="sr-only" {...input} />
			</label>
		</div>
	);
}

const weekLabel = (d: string) =>
	new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", {
		weekday: "short",
		day: "numeric",
		month: "short",
		timeZone: "UTC",
	});
const shiftWeek = (monday: string, weeks: number) => {
	const t = Date.UTC(Number(monday.slice(0, 4)), Number(monday.slice(5, 7)) - 1, Number(monday.slice(8, 10)));
	return new Date(t + weeks * 7 * 86_400_000).toISOString().slice(0, 10);
};

/** Previous week, the week's name, next week. On phones the name sits above two equal buttons. */
export function WeekSwitcher({ week, onChange }: { week: string; onChange: (monday: string) => void }) {
	return (
		<div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
			<p className="w-full font-display text-lg font-bold sm:order-2 sm:w-auto sm:px-2" aria-live="polite">
				Week of {weekLabel(week)}
			</p>
			<Button
				variant="secondary"
				className="flex-1 sm:order-1 sm:flex-none"
				onClick={() => onChange(shiftWeek(week, -1))}
			>
				Previous week
			</Button>
			<Button
				variant="secondary"
				className="flex-1 sm:order-3 sm:flex-none"
				onClick={() => onChange(shiftWeek(week, 1))}
			>
				Next week
			</Button>
		</div>
	);
}

/**
 * On or off for a setting that takes effect straight away ("Taking
 * registrations"). The words say what it does; the track says which way it is,
 * and "On" or "Off" is written beside it, so colour is never the only clue.
 */
export function Switch({
	label,
	hint,
	checked,
	onChange,
	disabled = false,
}: {
	label: ReactNode;
	hint?: ReactNode;
	checked: boolean;
	onChange: (on: boolean) => void;
	disabled?: boolean;
}) {
	const id = useId();
	return (
		<div className="flex min-h-12 items-center justify-between gap-4 py-2">
			<span className="min-w-0">
				<span id={`${id}-label`} className="block font-semibold">
					{label}
				</span>
				{hint ? (
					<span id={`${id}-hint`} className="block text-sm text-ink-soft">
						{hint}
					</span>
				) : null}
			</span>
			<button
				type="button"
				role="switch"
				aria-checked={checked}
				aria-labelledby={`${id}-label`}
				aria-describedby={hint ? `${id}-hint` : undefined}
				disabled={disabled}
				onClick={() => onChange(!checked)}
				className={`group inline-flex min-h-12 shrink-0 cursor-pointer items-center gap-2 rounded-full px-1 font-semibold disabled:opacity-50 ${press}`}
			>
				<span
					aria-hidden="true"
					className={`relative h-8 w-14 rounded-full border-2 border-b-4 transition-colors ${checked ? "border-purple bg-mint" : "border-field bg-sheet"}`}
				>
					<span
						className={`absolute top-0.5 h-5 w-5 rounded-full bg-card shadow-[inset_0_-2px_0_var(--color-rule)] transition-[left] ${checked ? "left-7 bg-navy" : "left-0.5"}`}
					/>
				</span>
				<span className="w-7 text-left text-sm">{checked ? "On" : "Off"}</span>
			</button>
		</div>
	);
}
